import axios from 'axios'
import {
  getCountryAsIso2,
  getLanguageCode,
  LANGUAGE_OPTIONS,
  LOCALE_TO_CRM_LANGUAGE,
} from '~/utils/msDynamics'

export default ({ app }, inject) => {
  const crmApi = {
    async sendFormData(formData) {
      try {
        const payload = prepareCrmPayload(formData)

        // Encode form data as base64
        const encodedData = Buffer.from(JSON.stringify(payload)).toString(
          'base64'
        )

        const response = await axios.get(process.env.CRM_LAMBDA_URL, {
          headers: {
            'Content-Type': 'application/json',
            'x-form-data': encodedData,
          },
        })
        return response.data
      } catch (error) {
        console.error('CRM API Error:', error)
        return null
      }
    },
  }

  const prepareCrmPayload = (formData) => {
    // Check if formData is an object and not empty
    if (typeof formData !== 'object' || !Object.keys(formData).length) {
      throw new Error('Invalid form data')
    }

    // Copy of formData to avoid mutation
    const processedData = { ...formData }

    // Remove captcha token
    if (processedData.captchaToken) delete processedData.captchaToken

    // Convert country code from iso3 to iso2
    if (processedData.country)
      processedData.country = convertCountryCode(processedData.country)

    // Convert language value to CRM code
    if (processedData.language) {
      const languageName = LANGUAGE_OPTIONS.find(
        (opt) => opt.value === processedData.language
      )?.language

      if (languageName) processedData.language = getLanguageCode(languageName)
    } else {
      // Get current locale from Nuxt app
      const currentLocale = app.i18n.locale
      processedData.language =
        LOCALE_TO_CRM_LANGUAGE[currentLocale] || LOCALE_TO_CRM_LANGUAGE.en
    }

    // Set default required values
    processedData.leadsourcecode = 1 // Default lead source code to 1 (1 = Landing Page)

    const additionalFields = handleAdditionalFields(processedData)
    const subscriptionsIds = handleSubscriptions(processedData)

    // Build final payload
    return {
      ...processedData,
      currentUrl: window.location.href,
      referrerUrl: document.referrer,
      fields: additionalFields,
      subscriptions: subscriptionsIds,
    }
  }

  // Check if formdata has newsletter checkbox checked, and include it in the subscriptions array
  // Otherwise, return an empty array
  const handleSubscriptions = (formData) => {
    if (!formData) return []

    if (formData.newsletter) {
      return ['0cdff69a-9ae7-ef11-be20-7c1e52343666'] // Hearpeers Newsletter subscription ID
    }

    return []
  }

  // Handle additional fields for CRM
  // Excluding fields that are not needed in the main CRM, and append them as additional fields
  const handleAdditionalFields = (formData) => {
    const excludedFields = new Set([
      'nlOnly',
      'category',
      'firstname',
      'lastname',
      'email',
      'phone',
      'country',
      'language',
      'currentUrl',
      'referrerUrl',
      'formId',
      'captchaValid',
      'leadsourcecode',
      'captchaToken',
      'subscriptions',
    ])

    // Return array of formatted strings
    return Object.entries(formData)
      .filter(([key]) => !excludedFields.has(key))
      .map(([key, value]) => `${key}: ${value}`)
  }

  // Handle country conversion from iso3 to iso2
  const convertCountryCode = (countryCode) => {
    if (countryCode && countryCode.length === 3) {
      const countryIso2 = getCountryAsIso2(countryCode)
      countryCode = countryIso2 || countryCode
    }
    return countryCode
  }

  inject('crm', crmApi)
}
